<template>
  <SettingsPageFull>
    <template #title>
      <div class="flex space-x-3">
        <font-awesome-icon :icon="['fal', 'user']" size="2x" />
        <span class="text-2xl font-bold capitalize-first">{{ pageTitle }}</span>
      </div>
    </template>
    <template #cta>
      <div class="flex gap-x-3">
        <div v-if="editionMode">
          <BaseButton
            :isElevated="true"
            icon="paper-plane"
            @click="submitForm"
            :title="buttonText"
          />
        </div>
        <BaseButton
          v-if="
            this.$route.name !== 'settings_create_user' &&
            this.$can('manage_content')
          "
          color="warning"
          :icon="editionMode ? 'times' : 'pen'"
          @click="switchEditMode"
          :title="editionMode ? $t('button.cancel') : $t('button.edit')"
        />
        <BaseButton
          type="dark"
          icon="long-arrow-left"
          @click="this.$router.go(-1)"
          :title="$t('button.back')"
        />
      </div>
    </template>
    <template #body>
      <div class="flex flex-col gap-4">
        <PageSection
          v-if="user"
          disable-sticky-margin
          :title="$t('global.general_information')"
        >
          <div class="grid-form">
            <div>
              <BaseShowEditInput
                :label="$t('attributes.first_name')"
                v-model="user.first_name"
                :errors="errors.first_name"
                :placeholder="$t('attributes.first_name')"
                :editionMode="editionMode"
                :required="true"
              />
            </div>
            <div>
              <BaseLanguageShowEditSelectSimple
                v-model="user.language"
                :metadata="metadata"
                :errors="errors"
                :edition-mode="editionMode"
              />
            </div>
            <div>
              <BaseShowEditInput
                :label="$t('attributes.last_name')"
                v-model="user.last_name"
                :errors="errors.last_name"
                :placeholder="$t('attributes.last_name')"
                :editionMode="editionMode"
                :required="true"
              />
            </div>
            <div>
              <BaseCompanyShowEditSelectMultiple
                v-if="
                  !isAuthCustomerUserOrNewCustomerUserView && metadata.companies
                "
                v-model="user.companies_id"
                :editionMode="editionMode"
                :metadata="metadata"
                :errors="errors"
                :required="true"
              />
            </div>
            <div>
              <BaseShowEditInput
                :label="$t('attributes.username')"
                v-model="user.user_name"
                :errors="errors.user_name"
                :placeholder="$t('attributes.username')"
                :editionMode="editionMode"
                :required="true"
              />
            </div>
            <div>
              <BaseShowEditPhoneInput
                :label="$t('attributes.mobile')"
                :editionMode="editionMode"
                name="mobile"
                v-model:countryValue="user.mobile_country"
                v-model:numberValue="user.mobile_field"
                :errors="errors"
                :metadata="metadata"
              />
            </div>
            <div>
              <BaseShowEditInput
                :label="$t('attributes.email')"
                v-model="user.email"
                :errors="errors.email"
                :placeholder="$t('attributes.email')"
                :editionMode="editionMode"
                :required="true"
              />
            </div>
            <div>
              <BaseShowEditPhoneInput
                :label="$t('attributes.phone')"
                :editionMode="editionMode"
                name="phone"
                v-model:countryValue="user.phone_country"
                v-model:numberValue="user.phone_field"
                :errors="errors"
                @change.self="getInputValue"
                :metadata="metadata"
              />
            </div>
            <div id="scroll_entry_date">
              <BaseShowEditDatePicker
                :editionMode="editionMode"
                label="date d'entrée"
                v-model="user.entry_date"
                class="z-10"
                name="entry_date"
                @click="scrollToDateFrom"
                :errors="errors.entry_date"
              />
            </div>
            <div id="scroll_exit_date">
              <BaseShowEditDatePicker
                :editionMode="editionMode"
                label="date de sortie"
                v-model="user.exit_date"
                class="z-10"
                name="exit_date"
                @click="scrollToDateFrom"
                :errors="errors.exit_date"
              />
            </div>
            <div v-if="editionMode" class="flex flex-col gap-3 md:col-span-2">
              <div>
                <BaseButton
                  v-if="this.$route.name === 'settings_edit_user_route'"
                  :type="editPasswordType"
                  :icon="editPasswordIcon"
                  @click="this.editPassword = !this.editPassword"
                  :title="$t(editPasswordText, { var: $t('global.user') })"
                />
              </div>
              <div v-if="editPassword" class="grid-form">
                <BaseShowEditInput
                  :label="$t('attributes.password')"
                  v-model="user.password"
                  :errors="errors.password"
                  :placeholder="$t('attributes.password')"
                  :editionMode="editionMode"
                  :required="true"
                />
                <BaseShowEditInput
                  :label="$t('attributes.password_confirmation')"
                  v-model="user.password_confirmation"
                  :errors="errors.password_confirmation"
                  :placeholder="$t('attributes.password_confirmation')"
                  :editionMode="editionMode"
                  :required="true"
                />
              </div>
            </div>
          </div>
        </PageSection>
        <PageSection
          v-if="!isAuthCustomerUserOrNewCustomerUserView && user.roles"
          disable-sticky-margin
          :title="$t('global.roles')"
        >
          <UserRoleListCheckboxes
            v-model="user.roles"
            :roles="metadata.roles ?? []"
            :edition-mode="isEditing"
            :errors="errors"
          />

          <div
            v-if="errors.roles"
            v-for="(error, index) in errors.roles"
            :key="index"
            class="form-help mt-2 text-red-600"
          >
            {{ $t(`${error}`, { attribute: $t('attributes.roles') }) }}
          </div>
        </PageSection>
        <PageSection
          v-if="!isAuthCustomerUserOrNewCustomerUserView"
          disable-sticky-margin
          :title="$t('global.permissions')"
        >
          <UserPermissionTable
            v-if="metadata.groupedPermissions"
            v-model="user.permissions"
            :grouped-permissions="metadata.groupedPermissions"
            :permissions-from-roles="permissionsFromRoles"
            :edition-mode="isEditing"
          />
        </PageSection>
      </div>
      <!--      <div class='flex justify-end' v-if='editionMode'>-->
      <!--        <BaseButton :isElevated='true' icon='paper-plane' @click='submitForm' :title="buttonText"/>-->
      <!--      </div>-->
    </template>
  </SettingsPageFull>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import UserPermissionTable from '@c/RolesAndPermissions/UserPermissionTable.vue'
import UserRoleListCheckboxes from '@c/RolesAndPermissions/UserRoleListCheckboxes.vue'

export default {
  name: 'TheUserFormPage',
  components: {
    UserPermissionTable,
    UserRoleListCheckboxes
  },
  data() {
    return {
      editionMode: false,
      flag: null,
      editPassword: false
    }
  },
  computed: {
    ...mapGetters({
      user: 'user/getUser',
      metadata: 'user/getUserMetadata',
      errors: 'auth/getErrors',
      authRole: 'auth/getAuthRole'
    }),
    isEditing() {
      return this.$route.name === 'settings_create_user' || this.editionMode
    },
    // The permissions the user's roles give, as FM's roles page.
    permissionsFromRoles() {
      const fromRoles = this.metadata?.permissionsFromRoles ?? {}
      return [
        ...new Set(
          (this.user.roles ?? []).flatMap((role) =>
            Object.values(fromRoles[role] ?? {})
          )
        )
      ]
    },
    pageTitle() {
      if (this.user.first_name) {
        return `${this.user.last_name} ${this.user.first_name}`
      } else {
        switch (this.$route.name) {
          case 'settings_create_user':
            return this.$t('button.add_new_m', { var: this.$t('global.user') })
          case 'settings_create_customer_user_route':
            return this.$t('button.add_new_m', {
              var: this.$t('global.customer')
            })
        }
      }
      return `${this.user.first_name} ${this.user.last_name}`
    },
    buttonText() {
      switch (this.$route.name) {
        case 'settings_edit_user_route':
          return this.user.customer_id
            ? this.$t('button.edit_new_mv', { var: this.$t('global.user') })
            : this.$t('button.edit_new_mv', { var: this.$t('global.user') })
        case 'settings_create_user':
          return this.$t('button.add_new_m', { var: this.$t('global.user') })
        case 'settings_create_customer_user_route':
          return this.$t('button.add_new_m', { var: this.$t('global.user') })
      }
      return this.$route.name === 'settings_create_user'
        ? 'button.add_new_m'
        : 'button.edit_new_mv'
    },
    isAuthCustomerUserOrNewCustomerUserView() {
      return (
        this.user.customer_id ||
        this.$route.name === 'settings_create_customer_user_route'
      )
    },
    editPasswordText() {
      return !this.editPassword
        ? 'modifier le mot de passe'
        : 'annuler la modification'
    },
    editPasswordIcon() {
      return !this.editPassword ? 'unlock' : 'times'
    },
    editPasswordType() {
      return !this.editPassword ? 'warning' : 'secondary'
    }
  },
  methods: {
    ...mapActions({
      fetchNewUser: 'user/fetchNewUser',
      fetchUserByID: 'user/fetchUserByID',
      postNewUser: 'user/storeNewUser',
      updateUser: 'user/updateUser'
    }),
    getInputValue({ attribute, value }) {
      this.user[attribute] = value
    },
    switchEditMode() {
      if (this.editionMode === true) {
        this.fetchUserByID({ id: this.$route.params.id })
      }
      this.editionMode = !this.editionMode
    },
    scrollToDateFrom() {
      if (this.$route.name === 'planning_page_route') {
        document.getElementById('scroll_date_from').scrollIntoView()
      }
    },
    submitForm() {
      switch (this.$route.name) {
        case 'settings_edit_user_route':
          if (this.user.customer_id) {
            this.updateUser({
              user: this.user,
              id: this.$route.params.id
            }).then(() => {
              this.$h.toastSuccess(
                this.$t('toasts.updated_m', {
                  var: this.$t('global.customer')
                })
              )
              this.editionMode = false
            })
          } else {
            this.updateUser({
              user: this.user,
              id: this.$route.params.id
            }).then(() => {
              this.$h.toastSuccess(
                this.$t('toasts.updated_m', { var: this.$t('global.user') })
              )
              this.editionMode = false
            })
          }
          break
        case 'settings_create_user':
          this.postNewUser({
            user: this.user
          }).then(() => {
            this.$h.toastSuccess(
              this.$t('toasts.created_m', { var: this.$t('global.user') })
            )
            this.editionMode = false
          })
          break
        case 'settings_create_customer_user_route':
          this.postNewUser({
            user: this.user
          }).then(() => {
            this.$h.toastSuccess(
              this.$t('toasts.created_m', {
                var: this.$t('global.customer')
              })
            )
            this.editionMode = false
          })
          break
      }
    }
  },

  async beforeMount() {
    switch (this.$route.name) {
      case 'settings_edit_user_route':
        await this.fetchUserByID({ id: this.$route.params.id })
        break
      case 'settings_create_user':
        await this.fetchNewUser()
        if (!Array.isArray(this.user.roles)) this.user.roles = []
        break
      case 'settings_create_customer_user_route':
        await this.fetchNewUser()
        this.user.customer_id = this.$route.params.id
        this.user.roles = ['customer-user']
        break
    }
  },
  mounted() {
    switch (this.$route.name) {
      case 'settings_edit_user_route':
        this.editPassword = false
        break
      case 'settings_create_user':
        this.editPassword = true
        this.editionMode = true
        break
      case 'settings_create_customer_user_route':
        this.editPassword = true
        this.editionMode = true
        break
    }
  }
}
</script>
